<template>
  <section class="user-activity-section">
    <div class="section-header">
      <h2 class="section-title">User Activity & Engagement</h2>
      <div class="time-filter">
        <select v-model="timeframe" class="filter-select">
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>
    </div>
    
    <div class="activity-grid">
      <!-- Growth Metrics -->
      <div class="activity-card">
        <h3 class="card-title">Growth Metrics</h3>
        <div class="metrics-list">
          <div class="metric-item">
            <div class="metric-info">
              <span class="metric-name">New Signups</span>
              <span class="metric-value">{{ activityData.growth.new_signups }}</span>
            </div>
            <div class="metric-trend" :class="getTrendClass(activityData.growth.signup_trend)">
              <span>{{ activityData.growth.signup_trend }}%</span>
              <svg v-if="activityData.growth.signup_trend > 0" xmlns="http://www.w3.org/2000/svg" class="trend-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="trend-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
          
          <div class="metric-item">
            <div class="metric-info">
              <span class="metric-name">User Retention</span>
              <span class="metric-value">{{ activityData.growth.retention_rate }}%</span>
            </div>
            <div class="metric-trend" :class="getTrendClass(activityData.growth.retention_trend)">
              <span>{{ activityData.growth.retention_trend }}%</span>
              <svg v-if="activityData.growth.retention_trend > 0" xmlns="http://www.w3.org/2000/svg" class="trend-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="trend-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
          
          <div class="metric-item">
            <div class="metric-info">
              <span class="metric-name">Average Session</span>
              <span class="metric-value">{{ activityData.engagement.avg_session_time }} min</span>
            </div>
            <div class="metric-trend" :class="getTrendClass(activityData.engagement.session_trend)">
              <span>{{ activityData.engagement.session_trend }}%</span>
              <svg v-if="activityData.engagement.session_trend > 0" xmlns="http://www.w3.org/2000/svg" class="trend-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="trend-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Engagement by Device -->
      <div class="activity-card">
        <h3 class="card-title">Device Usage</h3>
        <div class="device-chart">
          <div v-for="(device, index) in activityData.devices" :key="index" class="device-item">
            <div class="device-info">
              <span class="device-name">{{ device.name }}</span>
              <span class="device-percentage">{{ device.percentage }}%</span>
            </div>
            <div class="progress-bar">
              <div class="progress" :style="{ width: `${device.percentage}%`, backgroundColor: device.color }"></div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- User Geographic Distribution -->
      <div class="activity-card">
        <h3 class="card-title">Geographic Distribution</h3>
        <div class="country-list">
          <div v-for="(country, index) in activityData.geoDistribution" :key="index" class="country-item">
            <span class="country-name">{{ country.name }}</span>
            <span class="country-value">{{ country.users }}</span>
            <span class="country-percentage">{{ country.percentage }}%</span>
          </div>
        </div>
      </div>
      
      <!-- Popular Learning Times -->
      <div class="activity-card">
        <h3 class="card-title">Popular Learning Times</h3>
        <div class="hour-chart">
          <div v-for="hour in activityData.popularTimes" :key="hour.time" class="hour-bar"
               :style="{ height: `${(hour.value / maxHourValue) * 100}%` }"
               :class="{ 'most-active': hour.value === maxHourValue }">
            <div class="hour-tooltip">{{ hour.value }} users</div>
            <span class="hour-label">{{ hour.time }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'UserActivitySection',
  props: {
    // API data prop
    userData: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const timeframe = ref('30d');
    
    // Computed activity data that uses either API data or dummy data
    const activityData = computed(() => {
      if (props.userData && Object.keys(props.userData).length > 0) {
        return props.userData;
      }
      
      // Dummy data
      return {
        growth: {
          new_signups: 347,
          signup_trend: 8.5,
          retention_rate: 76,
          retention_trend: 2.3
        },
        engagement: {
          avg_session_time: 27,
          session_trend: 5.2,
          completion_rate: 68,
          completion_trend: -1.5
        },
        devices: [
          { name: 'Desktop', percentage: 58, color: '#4f46e5' },
          { name: 'Mobile', percentage: 32, color: '#10b981' },
          { name: 'Tablet', percentage: 10, color: '#f59e0b' }
        ],
        geoDistribution: [
          { name: 'United States', users: 4235, percentage: 34 },
          { name: 'India', users: 2018, percentage: 16 },
          { name: 'United Kingdom', users: 1427, percentage: 11 },
          { name: 'Canada', users: 1125, percentage: 9 },
          { name: 'Germany', users: 876, percentage: 7 }
        ],
        popularTimes: [
          { time: '6am', value: 250 },
          { time: '9am', value: 680 },
          { time: '12pm', value: 540 },
          { time: '3pm', value: 480 },
          { time: '6pm', value: 780 },
          { time: '9pm', value: 890 },
          { time: '12am', value: 320 }
        ]
      };
    });
    
    // Calculate max value for hour chart
    const maxHourValue = computed(() => {
      return Math.max(...activityData.value.popularTimes.map(item => item.value));
    });
    
    // Helper function to determine trend class
    const getTrendClass = (value) => {
      return value >= 0 ? 'trend-up' : 'trend-down';
    };
    
    return {
      timeframe,
      activityData,
      maxHourValue,
      getTrendClass
    };
  }
};
</script>

<style scoped>
.user-activity-section {
  @apply bg-white rounded-lg shadow p-6;
}

.section-header {
  @apply flex justify-between items-center mb-6;
}

.section-title {
  @apply text-xl font-semibold text-gray-800;
}

.time-filter {
  @apply relative;
}

.filter-select {
  @apply bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
  focus:ring-blue-500 focus:border-blue-500 block p-2.5;
}

.activity-grid {
  @apply grid grid-cols-1 lg:grid-cols-2 gap-6;
}

.activity-card {
  @apply bg-gray-50 rounded-lg p-5;
}

.card-title {
  @apply text-sm font-semibold text-gray-700 mb-4;
}

/* Metrics List Styles */
.metrics-list {
  @apply space-y-4;
}

.metric-item {
  @apply flex justify-between items-center p-3 bg-white rounded-lg;
}

.metric-info {
  @apply flex flex-col;
}

.metric-name {
  @apply text-sm text-gray-600;
}

.metric-value {
  @apply text-lg font-bold text-gray-800;
}

.metric-trend {
  @apply flex items-center text-sm font-medium;
}

.trend-icon {
  @apply h-4 w-4 ml-1;
}

.trend-up {
  @apply text-green-500;
}

.trend-down {
  @apply text-red-500;
}

/* Device Chart Styles */
.device-chart {
  @apply space-y-5;
}

.device-item {
  @apply space-y-1;
}

.device-info {
  @apply flex justify-between text-sm;
}

.device-name {
  @apply text-gray-700;
}

.device-percentage {
  @apply font-semibold text-gray-800;
}

.progress-bar {
  @apply bg-gray-200 rounded-full h-2.5 overflow-hidden;
}

.progress {
  @apply h-full rounded-full;
}

/* Country List Styles */
.country-list {
  @apply space-y-2;
}

.country-item {
  @apply flex justify-between items-center p-2 text-sm;
}

.country-name {
  @apply text-gray-700 flex-1;
}

.country-value {
  @apply text-gray-800 font-medium flex-1 text-right;
}

.country-percentage {
  @apply text-gray-500 w-12 text-right;
}

/* Hour Chart Styles */
.hour-chart {
  @apply flex justify-between items-end h-40;
}

.hour-bar {
  @apply relative w-[12.5%] bg-blue-400 rounded-t-md mx-1 transition-all duration-300 hover:bg-blue-500 cursor-pointer;
}

.hour-bar.most-active {
  @apply bg-blue-600;
}

.hour-tooltip {
  @apply absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full bg-gray-800 text-white text-xs py-1 px-2 rounded opacity-0 transition-opacity duration-200 pointer-events-none;
}

.hour-bar:hover .hour-tooltip {
  @apply opacity-100;
}

.hour-label {
  @apply absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full text-xs text-gray-600 pt-1;
}

@media (prefers-color-scheme: dark) {
  .user-activity-section {
    @apply bg-gray-800;
  }
  
  .section-title {
    @apply text-gray-100;
  }
  
  .filter-select {
    @apply bg-gray-700 border-gray-600 text-gray-100;
  }
  
  .activity-card {
    @apply bg-gray-700;
  }
  
  .card-title {
    @apply text-gray-300;
  }
  
  .metric-item {
    @apply bg-gray-800;
  }
  
  .metric-name {
    @apply text-gray-400;
  }
  
  .metric-value {
    @apply text-gray-200;
  }
  
  .device-name {
    @apply text-gray-300;
  }
  
  .device-percentage {
    @apply text-gray-200;
  }
  
  .progress-bar {
    @apply bg-gray-600;
  }
  
  .country-name {
    @apply text-gray-300;
  }
  
  .country-value {
    @apply text-gray-300;
  }
  
  .country-percentage {
    @apply text-gray-400;
  }
  
  .hour-label {
    @apply text-gray-400;
  }
}
</style> 